import React, { useState } from 'react';
import { isBlank } from 'utils/helperMethods';
import PropTypes from 'prop-types';
import { isNil, is, prop, find, head, propEq, always } from 'ramda';
import { TextField, InputAdornment } from '@material-ui/core';
import Autocomplete from '@material-ui/lab/Autocomplete';
import { useTranslation } from 'react-i18next';
import Icon from 'sharedComponents/Icon';
import usePlacesAutocomplete, { getGeocode } from 'use-places-autocomplete';

const LocationSelectInput = props => {
  const { onChange, className, defaultInputValue, searchIcon } = props;
  const [listOpen, setListOpen] = useState(false);
  const { t } = useTranslation();

  const requestOptions = {
    componentRestrictions: { country: 'us' },
    types: ['(cities)'],
  };

  const addressesTypeMapper = [
    {
      label: 'city',
      componentType: 'locality',
      field: 'long_name',
    },
    {
      label: 'state',
      componentType: 'administrative_area_level_1',
      field: 'short_name',
    },
  ];

  const {
    ready,
    value,
    suggestions: { data },
    setValue,
    clearSuggestions,
  } = usePlacesAutocomplete({
    requestOptions,
    debounce: 300,
    defaultValue: defaultInputValue,
  });

  const handleInput = (_e, newValue) => {
    setValue(newValue);
    setListOpen(true);
  };

  const buildLocation = addressLines => {
    return addressLines.reduce((acc, line) => {
      const type = head(line.types);
      const mapper = find(propEq('componentType', type))(addressesTypeMapper);
      if (isBlank(mapper)) return acc;

      const { label, field } = mapper;
      return { ...acc, [label]: line[field] };
    }, {});
  };

  const handleSelect = async (_e, newValue) => {
    if (isNil(newValue)) {
      onChange(null);
      return;
    }

    setValue(newValue.description, false);
    clearSuggestions();
    setListOpen(false);

    const geo = await getGeocode({ address: newValue.description });
    const addressLines = prop('address_components', head(geo));
    const location = buildLocation(addressLines);

    onChange(location);
  };

  const normalizeOptionLabel = option =>
    is(String, option) ? option.replace(', USA', '') : option.description.replace(', USA', '');

  return (
    <Autocomplete
      options={data}
      className={className}
      autoComplete
      value={value}
      disabled={!ready}
      open={value && data.length > 0 && listOpen}
      onChange={handleSelect}
      onInputChange={handleInput}
      getOptionSelected={always(false)}
      getOptionLabel={normalizeOptionLabel}
      forcePopupIcon={false}
      renderInput={params => (
        <TextField
          {...params}
          placeholder={t('sharedComponents.LocationSelectInput.inputPlaceholder')}
          variant="outlined"
          fullWidth
          InputProps={{
            ...params.InputProps,
            startAdornment: searchIcon ? (
              <InputAdornment position="start">
                <Icon name="searchLens" />
              </InputAdornment>
            ) : null,
          }}
        />
      )}
      renderOption={option => {
        return <div>{option.description.replace(', USA', '')}</div>;
      }}
    />
  );
};

LocationSelectInput.propTypes = {
  onChange: PropTypes.func.isRequired,
  className: PropTypes.string,
  searchIcon: PropTypes.bool,
  defaultInputValue: PropTypes.string,
};

LocationSelectInput.defaultProps = {
  className: '',
  defaultInputValue: null,
  searchIcon: false,
};

export default LocationSelectInput;



// WEBPACK FOOTER //
// ./src/sharedComponents/LocationSelectInput/LocationSelectInput.js